import { useRef } from "react";
import type { MouseEvent as ReactMouseEvent, ReactNode } from "react";

/** Wraps a dark/atmospheric section with drifting aurora blobs behind its content. */
export function Aurora() {
  return <div className="aurora" aria-hidden />;
}

/** Faint noise texture, so dark panels read as a designed surface rather than a flat gradient fill. */
export function Grain() {
  return <div className="grain" aria-hidden />;
}

/** Small motes drifting upward for atmosphere on dark surfaces. Purely decorative. */
export function Particles({ count = 8 }: { count?: number }) {
  const items = useRef(
    Array.from({ length: count }, (_, i) => ({
      left: `${((i * 37) % 100)}%`,
      delay: `${(i * 1.7) % 12}s`,
      duration: `${10 + (i % 5) * 2.4}s`,
      size: i % 4 === 0 ? 6 : 4,
    })),
  ).current;
  return (
    <div className="particle-field" aria-hidden>
      {items.map((p, i) => (
        <span
          key={i}
          className="particle"
          style={{
            left: p.left,
            width: p.size,
            height: p.size,
            animationDelay: p.delay,
            animationDuration: p.duration,
          }}
        />
      ))}
    </div>
  );
}

/**
 * Cursor-tracked radial glow. Pass directly as onMouseMove on any element
 * that also carries the `spotlight` class, so the glow works on the real
 * semantic tag (section, etc.) instead of an extra wrapping div.
 */
export function spotlightPointer(e: ReactMouseEvent<HTMLElement>) {
  const el = e.currentTarget;
  const r = el.getBoundingClientRect();
  el.style.setProperty("--mx", `${((e.clientX - r.left) / r.width) * 100}%`);
  el.style.setProperty("--my", `${((e.clientY - r.top) / r.height) * 100}%`);
}

/** Infinite horizontal ticker. Duplicates the item list once for a seamless loop. */
export function Marquee({ items, className = "" }: { items: string[]; className?: string }) {
  return (
    <div className={`marquee ${className}`} aria-hidden>
      <div className="marquee-track">
        {[...items, ...items].map((item, i) => (
          <span
            key={i}
            className="mx-6 flex shrink-0 items-center whitespace-nowrap font-serif text-lg"
          >
            {item}
            <span className="ml-6 text-mint">•</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/** Card that tilts toward the cursor in 3D and settles back smoothly on leave. */
export function TiltCard({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  function onMove(e: ReactMouseEvent<HTMLDivElement>) {
    const el = e.currentTarget;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    el.style.setProperty("--rx", String(px * 8));
    el.style.setProperty("--ry", String(py * -8));
  }
  function onLeave(e: ReactMouseEvent<HTMLDivElement>) {
    const el = e.currentTarget;
    el.style.setProperty("--rx", "0");
    el.style.setProperty("--ry", "0");
  }
  return (
    <div
      className={`tilt-card ${className}`}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
    >
      {children}
    </div>
  );
}
